"use client";

import { useCallback, useState } from "react";

import { getCookie } from "cookies-next";
import { useRouter } from "next/navigation";
import { FormattedMessage } from "react-intl";
import { toast } from "sonner";

import { Button } from "@/components/atoms/Button";
import { Text } from "@/components/atoms/Text";
import HideIcon from "@/icons/__generated__/Hide/Hide20";
import { updateListing } from "~/api/listing";
import { IListing } from "~/types/api-types";

interface PendingListingBannerProps {
  listingId: string;
  listingStatus: IListing["status"];
}

export const PendingListingBanner = ({
  listingId,
  listingStatus,
}: PendingListingBannerProps) => {
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const publishListingAPI = useCallback(async () => {
    const accessToken = getCookie("PSAccess");
    if (!accessToken) {
      toast.error(
        <FormattedMessage
          id="listing.generic_error_refresh_page_try_again"
          defaultMessage="Something went wrong. Please refresh the page and try again."
        />,
      );
      return;
    }

    try {
      setLoading(true);

      await updateListing({
        listingId,
        accessToken: accessToken as string,
        payload: { status: "PENDING" },
      });

      router.refresh();
    } catch (error) {
      setLoading(false);
      const errorMessage =
        error instanceof Error ? (
          error.message
        ) : (
          <FormattedMessage
            id="listing.generic_error_refresh_page_try_again"
            defaultMessage="Something went wrong. Please refresh the page and try again."
          />
        );
      toast.error(errorMessage);
    }
  }, [listingId, router]);

  return (
    <div className="ps-container-lg-margin tw-bg-gray-200 tw-py-32 tw-w-full">
      <div className="ps-row">
        <div className="col-xs-12 tw-flex tw-flex-wrap tw-justify-between">
          {"PENDING" === listingStatus ? (
            <>
              <Text prefix={<HideIcon />} typeface="body-2-m">
                <FormattedMessage
                  id="listing.listing_main.preview_banner_message_pending"
                  defaultMessage="Your listing is Pending. Only you can see this preview."
                />
              </Text>
              <Button
                appearance="purple"
                variant="underline-m"
                href="/mylistings"
              >
                <FormattedMessage
                  id="listing.listing_main.preview_banner_manage_listings_link"
                  defaultMessage="Manage Listings"
                />
              </Button>
            </>
          ) : (
            <>
              <Text prefix={<HideIcon />} typeface="body-2-m">
                <FormattedMessage
                  id="listing.listing_main.preview_banner_message_publish"
                  defaultMessage="This is a preview. Click Publish if you're ready to submit your listing for Peerspace review."
                />
              </Text>
              <div className="tw-flex tw-flex-wrap tw-gap-24">
                <Button
                  appearance="black"
                  variant="underline-m"
                  href={`/mylistings/${listingId}`}
                >
                  <FormattedMessage
                    id="listing.listing_main.preview_banner_edit"
                    defaultMessage="Edit"
                  />
                </Button>
                <Button
                  appearance="purple"
                  showSpinner={loading}
                  onClick={publishListingAPI}
                >
                  <FormattedMessage
                    id="listing.listing_main.preview_banner_link_publish"
                    defaultMessage="Publish"
                  />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
